import type { Vector3 } from "three";
import type { SugarModel } from "../../loader/SugarModel";

/**
 * Pluggable position constraint for an in-progress product drag.
 * Configurator joints and Room Designer walls supply separate implementations.
 */
export interface DragSessionConstraint {
  onDragStart(model: SugarModel): void;
  /** After all onDragStart callbacks; for late transform side-effects. */
  onLateDragStart?(model: SugarModel): void;
  /** Transform candidate position; model.position may not be updated yet. */
  apply(model: SugarModel, position: Vector3): Vector3;
  onDragEnd(model: SugarModel): void;
}

/**
 * Shared product-drag lifecycle port.
 * Preview visuals and domain constraints stay product-owned.
 */
export interface DragSessionPort<
  TBegin = void,
  TMove = void,
  TCommit = void,
> {
  begin(input: TBegin): void;
  move(input: TMove): void;
  commit(input: TCommit): void | Promise<void>;
  cancel(): void;
}

/** Applies {@link DragSessionConstraint}s in registration order. */
export class DragConstraintPipeline {
  private _constraints: DragSessionConstraint[] = [];

  add(c: DragSessionConstraint): this {
    this._constraints.push(c);
    return this;
  }

  remove(c: DragSessionConstraint): void {
    this._constraints = this._constraints.filter((x) => x !== c);
  }

  onDragStart(model: SugarModel): void {
    for (const c of this._constraints) c.onDragStart(model);
    for (const c of this._constraints) c.onLateDragStart?.(model);
  }

  resolve(model: SugarModel, candidate: Vector3): Vector3 {
    let pos = candidate.clone();
    for (const c of this._constraints) {
      pos = c.apply(model, pos);
    }
    return pos;
  }

  onDragEnd(model: SugarModel): void {
    for (const c of this._constraints) c.onDragEnd(model);
  }
}
